import React, { useEffect, useState } from 'react';
import {
    Box,
    Grid,
    Typography,
    Paper,
    CircularProgress,
    Alert,
    useTheme,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useMaterialUIController } from 'context';
import DashboardLayout from "components/ui/LayoutContainers/DashboardLayout";
import DashboardNavbar from 'components/ui/Navbars/DashboardNavbar';
import MDBox from 'components/MDBox';
// Icons
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import SavingsIcon from '@mui/icons-material/Savings';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import BarChartIcon from '@mui/icons-material/BarChart';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

import NetworkManager from '../../app/network/networkManager';
import KPICard from './components/KPICard';
import UserGrowthChart from './components/UserGrowthChart';
import PointEconomyChart from './components/PointEconomyChart';
import TopVideosCard from './components/TopVideosCard';
import UserSegmentChart from './components/UserSegmentChart';
import ReportsBarChart from 'components/ui/Charts/BarCharts/ReportsBarChart';
import ReportsLineChart from 'components/ui/Charts/LineCharts/ReportsLineChart';
import {
    DashboardStats,
    UserGrowth,
    PointEconomy,
    UserAnalytics,
    UserSegment,
    PointAnalytics,
    StepAnalytics,
} from '../../app/network/api/dashboard';
import AdStatsWidget from './components/AdStatsWidget';

const DashboardScreen: React.FC = () => {
    const theme = useTheme();
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    // Fetch dashboard stats
    const {
        data: stats,
        isLoading: statsLoading,
        error: statsError,
    } = useQuery<DashboardStats>({
        queryKey: ['dashboardStats'],
        queryFn: async () => {
            const response = await NetworkManager.call('Dashboard', 'getStats', {});
            return response.data;
        },
        refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    });

    // Fetch user growth data
    const {
        data: userGrowth,
        isLoading: userGrowthLoading,
    } = useQuery<UserGrowth[]>({
        queryKey: ['userGrowth'],
        queryFn: async () => {
            const response = await NetworkManager.call('Dashboard', 'getUserGrowth', { days: 30 });
            return response.data;
        },
        refetchInterval: 5 * 60 * 1000,
    });

    // Fetch point economy data
    const {
        data: pointEconomy,
        isLoading: pointEconomyLoading,
    } = useQuery<PointEconomy[]>({
        queryKey: ['pointEconomy'],
        queryFn: async () => {
            const response = await NetworkManager.call('Dashboard', 'getPointEconomy', { days: 30 });
            return response.data;
        },
        refetchInterval: 5 * 60 * 1000,
    });

    // Fetch user segments
    const {
        data: userSegments,
        isLoading: userSegmentsLoading,
    } = useQuery<UserSegment[]>({
        queryKey: ['userSegments'],
        queryFn: async () => {
            const response = await NetworkManager.call('Dashboard', 'getUserSegments', {});
            return response.data;
        },
        refetchInterval: 5 * 60 * 1000,
    });

    // Fetch user analytics
    const {
        data: userAnalytics,
        isLoading: analyticsLoading,
    } = useQuery<UserAnalytics>({
        queryKey: ['userAnalytics'],
        queryFn: async () => {
            const response = await NetworkManager.call('Dashboard', 'getUserAnalytics', {});
            return response.data;
        },
        refetchInterval: 5 * 60 * 1000,
    });

    // Fetch point analytics
    const {
        data: pointAnalytics,
        isLoading: pointAnalyticsLoading,
    } = useQuery<PointAnalytics>({
        queryKey: ['pointAnalytics'],
        queryFn: async () => {
            const response = await NetworkManager.call('Dashboard', 'getPointAnalytics', {});
            return response.data;
        },
        refetchInterval: 5 * 60 * 1000,
    });

    // Fetch step analytics
    const {
        data: stepAnalytics,
        isLoading: stepAnalyticsLoading,
    } = useQuery<StepAnalytics>({
        queryKey: ['stepAnalytics'],
        queryFn: async () => {
            const response = await NetworkManager.call('Dashboard', 'getStepAnalytics', {});
            return response.data;
        },
        refetchInterval: 5 * 60 * 1000,
    });

    if (statsError) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox pt={6} pb={3}>
                    <Alert severity="error">
                        대시보드 데이터를 불러오는데 실패했습니다. 잠시 후 다시 시도해주세요.
                    </Alert>
                </MDBox>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <>
                    {/* User Metrics */}

                    <Box sx={{ mb: 4 }}>
                        <Box
                            sx={{
                                backgroundColor: darkMode ? '#1e293b' : '#f1f5f9',
                                borderLeft: '4px solid #1976d2',
                                borderRadius: 1,
                                p: 2,
                                mb: 3,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1.5,
                            }}
                        >
                            <PeopleIcon sx={{ fontSize: 24, color: '#1976d2' }} />
                            <Typography variant="h5" sx={{ fontWeight: 600, m: 0 }}>
                                사용자 현황
                            </Typography>
                        </Box>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={3}>
                                <KPICard
                                    title="전체 사용자"
                                    value={stats?.totalUsers || 0}
                                    loading={statsLoading}
                                    color="primary"
                                    icon={<PeopleIcon sx={{ fontSize: 28 }} />}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <KPICard
                                    title="신규 사용자 (오늘)"
                                    value={stats?.newUsersToday || 0}
                                    loading={statsLoading}
                                    trend={stats?.newUsersTrend && stats.newUsersTrend > 0 ? 'up' : stats?.newUsersTrend && stats.newUsersTrend < 0 ? 'down' : 'neutral'}
                                    trendValue={stats?.newUsersTrend ? `${Math.abs(stats.newUsersTrend)}명` : undefined}
                                    color="success"
                                    icon={<PersonAddIcon sx={{ fontSize: 28 }} />}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <KPICard
                                    title="활성 사용자 (오늘)"
                                    value={stats?.activeUsersToday || 0}
                                    loading={statsLoading}
                                    subtitle="DAU"
                                    color="info"
                                    icon={<TrendingUpIcon sx={{ fontSize: 28 }} />}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <KPICard
                                    title="비활성 사용자"
                                    value={stats?.inactiveUsers || 0}
                                    loading={statsLoading}
                                    subtitle="7일 이상 미접속"
                                    color="warning"
                                    icon={<PersonOffIcon sx={{ fontSize: 28 }} />}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    {/* User Analytics Charts */}
                    <Box sx={{ mt: 7, mb: 4 }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6} lg={4}>
                                <ReportsBarChart
                                    color="info"
                                    title="일간 신규 사용자"
                                    description="최근 7일"
                                    date="매일 업데이트"
                                    loading={analyticsLoading}
                                    chart={{
                                        labels: userAnalytics?.dailyData?.labels || [],
                                        datasets: userAnalytics?.dailyData?.datasets?.[0] || { label: '', data: [] }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={4}>
                                <ReportsLineChart
                                    color="success"
                                    title="월간 신규 사용자"
                                    description="최근 12개월"
                                    date="매월 업데이트"
                                    loading={analyticsLoading}
                                    chart={{
                                        labels: userAnalytics?.monthlyData?.labels || [],
                                        datasets: userAnalytics?.monthlyData?.datasets?.[0] || { label: '', data: [] }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={4}>
                                <ReportsLineChart
                                    color="dark"
                                    title="당월 일별 사용자"
                                    description="이번 달"
                                    date="매일 업데이트"
                                    loading={analyticsLoading}
                                    chart={{
                                        labels: userAnalytics?.dailyOfMonthData?.labels || [],
                                        datasets: userAnalytics?.dailyOfMonthData?.datasets?.[0] || { label: '', data: [] }
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Point Economy */}
                    <Box sx={{ mb: 4 }}>
                        <Box
                            sx={{
                                backgroundColor: darkMode ? '#1e293b' : '#f1f5f9',
                                borderLeft: '4px solid #1976d2',
                                borderRadius: 1,
                                p: 2,
                                mb: 3,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1.5,
                            }}
                        >
                            <AccountBalanceWalletIcon sx={{ fontSize: 24, color: '#1976d2' }} />
                            <Typography variant="h5" sx={{ fontWeight: 600, m: 0 }}>
                                포인트 현황
                            </Typography>
                        </Box>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={3}>
                                <KPICard
                                    title="총 발행 포인트"
                                    value={stats?.totalPointsIssued?.toLocaleString() || 0}
                                    loading={statsLoading}
                                    trend={stats?.totalPointsIssuedTrend && stats.totalPointsIssuedTrend > 0 ? 'up' : stats?.totalPointsIssuedTrend && stats.totalPointsIssuedTrend < 0 ? 'down' : 'neutral'}
                                    trendValue={stats?.totalPointsIssuedTrend ? `${Math.abs(stats.totalPointsIssuedTrend).toLocaleString()}P` : undefined}
                                    color="primary"
                                    icon={<AccountBalanceWalletIcon sx={{ fontSize: 28 }} />}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <KPICard
                                    title="총 사용 포인트"
                                    value={stats?.totalPointsUsed?.toLocaleString() || 0}
                                    loading={statsLoading}
                                    trend={stats?.totalPointsUsedTrend && stats.totalPointsUsedTrend > 0 ? 'up' : stats?.totalPointsUsedTrend && stats.totalPointsUsedTrend < 0 ? 'down' : 'neutral'}
                                    trendValue={stats?.totalPointsUsedTrend ? `${Math.abs(stats.totalPointsUsedTrend).toLocaleString()}P` : undefined}
                                    color="error"
                                    icon={<RemoveCircleOutlineIcon sx={{ fontSize: 28 }} />}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <KPICard
                                    title="유통 포인트"
                                    value={stats?.circulatingPoints || 0}
                                    loading={statsLoading}
                                    subtitle="현재 잔액"
                                    color="success"
                                    icon={<SavingsIcon sx={{ fontSize: 28 }} />}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <KPICard
                                    title="대기 중인 사용 요청"
                                    value={stats?.pendingUseRequests || 0}
                                    loading={statsLoading}
                                    color="warning"
                                    icon={<PendingActionsIcon sx={{ fontSize: 28 }} />}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Point Analytics Charts */}
                    <Box sx={{ mt: 7, mb: 4 }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6} lg={4}>
                                <ReportsBarChart
                                    color="primary"
                                    title="일간 포인트 적립"
                                    description="최근 7일"
                                    date="매일 업데이트"
                                    loading={pointAnalyticsLoading}
                                    chart={{
                                        labels: pointAnalytics?.dailyData?.labels || [],
                                        datasets: pointAnalytics?.dailyData?.datasets?.[0] || { label: '', data: [] }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={4}>
                                <ReportsLineChart
                                    color="warning"
                                    title="월간 포인트 적립"
                                    description="최근 12개월"
                                    date="매월 업데이트"
                                    loading={pointAnalyticsLoading}
                                    chart={{
                                        labels: pointAnalytics?.monthlyData?.labels || [],
                                        datasets: pointAnalytics?.monthlyData?.datasets?.[0] || { label: '', data: [] }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={4}>
                                <ReportsLineChart
                                    color="info"
                                    title="당월 일별 포인트"
                                    description="이번 달"
                                    date="매일 업데이트"
                                    loading={pointAnalyticsLoading}
                                    chart={{
                                        labels: pointAnalytics?.dailyOfMonthData?.labels || [],
                                        datasets: pointAnalytics?.dailyOfMonthData?.datasets?.[0] || { label: '', data: [] }
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Activity Tracking */}
                    <Box sx={{ mb: 4 }}>
                        <Box
                            sx={{
                                backgroundColor: darkMode ? '#1e293b' : '#f1f5f9',
                                borderLeft: '4px solid #1976d2',
                                borderRadius: 1,
                                p: 2,
                                mb: 3,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1.5,
                            }}
                        >
                            <DirectionsWalkIcon sx={{ fontSize: 24, color: '#1976d2' }} />
                            <Typography variant="h5" sx={{ fontWeight: 600, m: 0 }}>
                                활동 추적
                            </Typography>
                        </Box>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={4}>
                                <KPICard
                                    title="총 걸음 수 (오늘)"
                                    value={stats?.totalStepsToday || 0}
                                    loading={statsLoading}
                                    color="primary"
                                    icon={<DirectionsWalkIcon sx={{ fontSize: 28 }} />}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <KPICard
                                    title="평균 걸음 수/사용자"
                                    value={stats?.averageStepsPerUser?.toFixed(0) || 0}
                                    loading={statsLoading}
                                    color="info"
                                    icon={<BarChartIcon sx={{ fontSize: 28 }} />}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <KPICard
                                    title="목표 달성률"
                                    value={`${stats?.goalAchievementRate?.toFixed(1) || 0}%`}
                                    loading={statsLoading}
                                    color="success"
                                    icon={<EmojiEventsIcon sx={{ fontSize: 28 }} />}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Step Analytics Charts */}
                    <Box sx={{ mt: 7, mb: 4 }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6} lg={4}>
                                <ReportsBarChart
                                    color="success"
                                    title="일간 걸음 수"
                                    description="최근 7일"
                                    date="매일 업데이트"
                                    loading={stepAnalyticsLoading}
                                    chart={{
                                        labels: stepAnalytics?.dailyData?.labels || [],
                                        datasets: stepAnalytics?.dailyData?.datasets?.[0] || { label: '', data: [] }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={4}>
                                <ReportsLineChart
                                    color="primary"
                                    title="월간 걸음 수"
                                    description="최근 12개월"
                                    date="매월 업데이트"
                                    loading={stepAnalyticsLoading}
                                    chart={{
                                        labels: stepAnalytics?.monthlyData?.labels || [],
                                        datasets: stepAnalytics?.monthlyData?.datasets?.[0] || { label: '', data: [] }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={4}>
                                <ReportsLineChart
                                    color="dark"
                                    title="당월 일별 걸음"
                                    description="이번 달"
                                    date="매일 업데이트"
                                    loading={stepAnalyticsLoading}
                                    chart={{
                                        labels: stepAnalytics?.dailyOfMonthData?.labels || [],
                                        datasets: stepAnalytics?.dailyOfMonthData?.datasets?.[0] || { label: '', data: [] }
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Video Rehabilitation */}
                    <Box sx={{ mb: 4 }}>
                        <Box
                            sx={{
                                backgroundColor: darkMode ? '#1e293b' : '#f1f5f9',
                                borderLeft: '4px solid #1976d2',
                                borderRadius: 1,
                                p: 2,
                                mb: 3,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1.5,
                            }}
                        >
                            <OndemandVideoIcon sx={{ fontSize: 24, color: '#1976d2' }} />
                            <Typography variant="h5" sx={{ fontWeight: 600, m: 0 }}>
                                영상 재활
                            </Typography>
                        </Box>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={6}>
                                <KPICard
                                    title="영상 완료 (오늘)"
                                    value={stats?.videoCompletionsToday || 0}
                                    loading={statsLoading}
                                    color="primary"
                                    icon={<OndemandVideoIcon sx={{ fontSize: 28 }} />}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6}>
                                <KPICard
                                    title="평균 완료율"
                                    value={`${stats?.averageCompletionRate?.toFixed(1) || 0}%`}
                                    loading={statsLoading}
                                    color="success"
                                    icon={<CheckCircleIcon sx={{ fontSize: 28 }} />}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TopVideosCard
                                    videos={stats?.topVideos || []}
                                    loading={statsLoading}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Charts */}
                    <Box sx={{ mb: 4 }}>
                        <Box
                            sx={{
                                backgroundColor: darkMode ? '#1e293b' : '#f1f5f9',
                                borderLeft: '4px solid #1976d2',
                                borderRadius: 1,
                                p: 2,
                                mb: 3,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1.5,
                            }}
                        >
                            <BarChartIcon sx={{ fontSize: 24, color: '#1976d2' }} />
                            <Typography variant="h5" sx={{ fontWeight: 600, m: 0 }}>
                                추이 분석
                            </Typography>
                        </Box>
                        <Grid container spacing={3}>
                            <Grid item xs={12} lg={6}>
                                <UserGrowthChart
                                    data={userGrowth || []}
                                    loading={userGrowthLoading}
                                />
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <PointEconomyChart
                                    data={pointEconomy || []}
                                    loading={pointEconomyLoading}
                                />
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <UserSegmentChart
                                    data={userSegments || []}
                                    loading={userSegmentsLoading}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Ad Stats Widget */}
                    <Box sx={{ mb: 4 }}>
                        <AdStatsWidget />
                    </Box>
                </>

            </MDBox>
        </DashboardLayout>
    );
};

export default DashboardScreen;
